import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { AlertTriangle, CheckCircle2, Circle, CircleDot } from "lucide-react";

const formatWhen = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
};

const buildSteps = (order) => {
  if (!order) return [];
  const verification = order.verification_status || "pending";
  const isVerified = verification === "verified" || verification === "customer_verified";
  const isMismatch = verification === "mismatched";
  const confirmed = verification === "customer_verified" || Boolean(order.customer_verified_at);
  const paid = Boolean(order.report_paid);
  const refunded = order.admin_status === "refunded";
  const processing = ["paid", "processing"].includes(order.admin_status);
  const delivered = Boolean(order.report_uploaded_at) || order.admin_status === "completed";

  const extractionStep = () => {
    let detail;
    if (isVerified) {
      detail = "Extracted data matched your entries";
    } else if (isMismatch) {
      detail = order.mismatch_reason || "A mismatch was flagged for your review";
    } else {
      detail = "Our system is reading and comparing your document";
    }
    let state;
    if (isVerified || confirmed) {
      state = "done";
    } else if (isMismatch) {
      state = "attention";
    } else {
      state = "current";
    }
    return { detail, state };
  };

  const confirmationStep = () => {
    if (confirmed) {
      return { detail: "You confirmed the final details", state: "done" };
    }
    if (isMismatch) {
      return { detail: "Action needed: review the mismatch from your dashboard", state: "attention" };
    }
    if (isVerified) {
      return { detail: "Confirm the details to make this order payable", state: "current" };
    }
    return { detail: "Available after the document check", state: "upcoming" };
  };

  const paymentStep = () => {
    if (refunded) {
      return { detail: `Refunded ${formatWhen(order.refunded_at)}`, state: "attention" };
    }
    if (paid) {
      return { detail: "Payment received — thank you", state: "done" };
    }
    if (confirmed) {
      return { detail: "Add this order to a report checkout when ready", state: "current" };
    }
    return { detail: "Available after confirmation", state: "upcoming" };
  };

  const processingStep = () => {
    if (delivered) {
      return { detail: "Processing complete", state: "done" };
    }
    if (processing && paid && !refunded) {
      return {
        detail: "Our operations team is preparing your report (30-60 days typical)",
        state: "current",
      };
    }
    if (processing && paid) {
      return {
        detail: "Our operations team is preparing your report (30-60 days typical)",
        state: "upcoming",
      };
    }
    return { detail: "Starts once payment is received", state: "upcoming" };
  };

  const deliveredStep = () => {
    if (delivered) {
      return {
        detail: `Finished report delivered ${formatWhen(order.report_uploaded_at)} — emailed and available in the Reports column`,
        state: "done",
      };
    }
    return { detail: "You will be emailed the finished report", state: "upcoming" };
  };

  return [
    {
      key: "submitted",
      title: "Submitted",
      detail: `Document received ${formatWhen(order.created_at)}`,
      state: "done",
    },
    { key: "extraction", title: "Document check", ...extractionStep() },
    { key: "confirmation", title: "Your confirmation", ...confirmationStep() },
    { key: "payment", title: refunded ? "Payment (refunded)" : "Payment", ...paymentStep() },
    { key: "processing", title: "Operations processing", ...processingStep() },
    { key: "delivered", title: "Report delivered", ...deliveredStep() },
  ];
};

const STEP_ICON = {
  done: <CheckCircle2 className="h-5 w-5 text-[hsl(var(--status-verified))]" />,
  current: <CircleDot className="h-5 w-5 text-primary animate-pulse" />,
  attention: <AlertTriangle className="h-5 w-5 text-[hsl(var(--status-mismatch))]" />,
  upcoming: <Circle className="h-5 w-5 text-muted-foreground/50" />,
};

export const OrderTimelineDialog = ({ order, open, onOpenChange }) => {
  const steps = buildSteps(order);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="order-timeline-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2.5">
            Order progress
            {order && <span className="font-mono text-sm text-muted-foreground">{order.id?.slice(0, 8)}</span>}
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2">
            {order && (
              <>
                <span className="capitalize">
                  {String((order.entered || {}).request_type || "").replaceAll("_", " ")} ·{" "}
                  <span className="font-mono">{(order.entered || {}).identifier_value}</span>
                </span>
                <StatusBadge status={order.verification_status} />
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <ol className="space-y-0" data-testid="order-timeline-steps">
          {steps.map((step, index) => (
            <li key={step.key} className="relative flex gap-3 pb-5 last:pb-0" data-testid={`timeline-step-${step.key}`}>
              {index < steps.length - 1 && (
                <span
                  className={`absolute left-[9px] top-6 h-full w-0.5 ${
                    step.state === "done" ? "bg-[hsl(var(--status-verified))]/40" : "bg-border"
                  }`}
                  aria-hidden="true"
                />
              )}
              <span className="relative z-10 mt-0.5 shrink-0 bg-background">{STEP_ICON[step.state]}</span>
              <div className="min-w-0">
                <p className={`text-sm font-medium ${step.state === "upcoming" ? "text-muted-foreground" : ""}`}>
                  {step.title}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">{step.detail}</p>
              </div>
            </li>
          ))}
        </ol>
      </DialogContent>
    </Dialog>
  );
};
